import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { modalPages as modals } from 'constants/modalPage';
import { setGridView } from 'actions/UI';
import { displayView } from 'constants/UI';
import { openModalPage } from 'actions/modalPage';
import { getSetting, getUISetting } from 'reducers/configs/settings';
import { getSelectedProductGroupSubgroups } from 'reducers/productGroupsDB';
import { useMeasureListHeight } from 'containers/Col2/GridViews/util';
import { getCanAddProducts } from 'reducers/Login';
import { getSelectedWarehouseID } from 'reducers/warehouses';
import useProducts from 'utils/hooks/useProducts';

import ProductGroups from '../ProductGroups';
import Products from '../Products';
import Functions from '../Functions';
import SaleOptions from '../SaleOptions';

import { addNewBttn, extendBttn, homeBttn } from './SpecialBttns';

const MainView = () => {
  const dispatch = useDispatch();
  const warehouseID = useSelector(getSelectedWarehouseID);
  const getItemsFromFirstPriceListOnly = useSelector(
    getSetting('pos_allow_selling_only_from_pricelist1'),
  );

  const setGridViewToProducts = useCallback(
    () => dispatch(setGridView(displayView.productGroups)),
    [displayView.productGroups],
  );

  const setGridViewToQuickSelect = useCallback(
    () => dispatch(setGridView(displayView.quickSelect)),
    [displayView.quickSelect],
  );

  const createNewItem = useCallback(
    () =>
      dispatch(
        openModalPage({
          component: modals.createNew,
        }),
      ),
    [],
  );
  const canAddProducts = useSelector(getCanAddProducts);
  const hideAddProductBtn = useSelector(getUISetting('hideAddProductOrGroup'));

  const startBttns = useMemo(
    () =>
      [
        canAddProducts > 0 && !hideAddProductBtn && addNewBttn(createNewItem),
      ].filter(a => a),
    [canAddProducts, hideAddProductBtn],
  );

  const productGroups = useSelector(getSelectedProductGroupSubgroups);

  const { products } = useProducts({
    getProductsFor: 'SALES',
    warehouseID,
    getItemsFromFirstPriceListOnly,
    getWarehouseSpecificVAT: 1,
    quickPosProducts: 1,
  });

  const [functionLines, setFunctionLines] = useState(2);
  const [saleOptLines, setSaleOptLines] = useState(2);
  const [view, setView] = useState(null);
  const goHomeBttns = useMemo(() => [homeBttn(() => setView(null))], [
    homeBttn,
    setView,
  ]);
  const productGroupsExtendBttns = useMemo(
    () => [extendBttn(setGridViewToProducts)],
    [extendBttn],
  );
  const quickSelectExtendBttns = useMemo(
    () => [extendBttn(setGridViewToQuickSelect)],
    [extendBttn],
  );
  const functionsExtendBttns = useMemo(
    () => [extendBttn(() => setView('functions'))],
    [extendBttn],
  );
  const saleOptionsExtendBttns = useMemo(
    () => [extendBttn(() => setView('options'))],
    [extendBttn],
  );
  const onSetNrFunctionLines = l => setFunctionLines(l);
  const onSetNrSaleOptionsLines = l => setSaleOptLines(l);

  const { needsToMeasure, Measure, totalLines } = useMeasureListHeight();
  if (needsToMeasure) return Measure;
  const remainingSpace =
    Math.max(7, totalLines) - saleOptLines - functionLines - 1;

  switch (view) {
    case 'functions':
      return (
        <Functions
          minLines={totalLines}
          maxLines={totalLines}
          specialBttnsStart={goHomeBttns}
        />
      );
    case 'options':
      return (
        <SaleOptions
          minLines={totalLines}
          maxLines={totalLines}
          specialBttnsStart={goHomeBttns}
        />
      );
    default:
      return (
        <div>
          <ProductGroups
            specialBttnsStart={startBttns}
            specialBttnsEndIfFull={productGroupsExtendBttns}
            productGroups={productGroups}
            minLines={1}
            maxLines={1}
          />
          <Products
            specialBttnsEndIfFull={quickSelectExtendBttns}
            products={products}
            minLines={remainingSpace}
            maxLines={remainingSpace}
          />
          <Functions
            maxLines={2}
            onSetNrLines={onSetNrFunctionLines}
            specialBttnsEndIfFull={functionsExtendBttns}
          />
          <SaleOptions
            maxLines={2}
            onSetNrLines={onSetNrSaleOptionsLines}
            specialBttnsEndIfFull={saleOptionsExtendBttns}
          />
        </div>
      );
  }
};

export default MainView;
